import { useEffect, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import axiosServices, { fetcher } from 'utils/axios';
import { formatDateKeyWithTimezone } from 'utils/dateUtils';
import { resolveShiftApiErrorMessage } from 'utils/shiftApiError';
import { buildCreateShiftRequestPayload, buildUpdateShiftRequestPayload } from 'utils/shiftPayload';

import { useTranslation } from 'hooks/useTranslation';
import type {
  CalendarShift,
  CreateShiftParams,
  CreateShiftResponse,
  CreateUnavailabilityParams,
  DeleteShiftResponse,
  DeleteUnavailabilityResponse,
  GetRestaurantShiftsParams,
  RestaurantShift,
  RestaurantShiftsResponse,
  ShiftType,
  Unavailability,
  UnavailabilityResponse,
  UpdateShiftParams,
  UpdateShiftResponse,
  UpdateUnavailabilityParams
} from 'types/shifts';
import type { ScheduleRequestCreateData, ScheduleRequestResponse, ShiftRequestType } from 'types/waiter';

interface ShiftsListParams {
  waiterId: string | number;
  from?: string;
  to?: string;
  timezone?: string | null;
}

export function useShiftsList(params: ShiftsListParams) {
  const { waiterId, from, to, timezone = null } = params;
  const queryParams = new URLSearchParams();

  if (from) queryParams.set('from', from);
  if (to) queryParams.set('to', to);

  const { data, isLoading, error, isValidating } = useSWR(`/waiters/${waiterId}/shifts?${queryParams.toString()}`, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(() => {
    const shifts = data?.data as ShiftType[];

    const shiftsByDate: Record<string, ShiftType[]> = {};
    const highlightedDates: string[] = [];

    shifts?.forEach((shift) => {
      const date = formatDateKeyWithTimezone(shift.start_at, timezone);

      if (!shiftsByDate[date]) {
        shiftsByDate[date] = [];
      }
      shiftsByDate[date].push(shift);

      if (!highlightedDates.includes(date)) {
        highlightedDates.push(date);
      }
    });

    const calendarShifts: CalendarShift[] = Object.entries(shiftsByDate).map(([date, dateShifts]) => ({
      id: parseInt(date.replace(/-/g, '')),
      date,
      shifts: dateShifts,
      hasShifts: true
    }));

    return {
      shifts: shifts || [],
      shiftsByDate,
      calendarShifts,
      highlightedDates,
      shiftsLoading: isLoading,
      shiftsError: error,
      shiftsValidating: isValidating,
      shiftsEmpty: !isLoading && !shifts?.length
    };
  }, [data, error, isLoading, isValidating, timezone]);

  return memoizedValue;
}

// ==============================|| SCHEDULE REQUEST HOOK ||============================== //

export function useScheduleRequest() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitRequest = async (data: ScheduleRequestCreateData): Promise<ScheduleRequestResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosServices.post('/schedule-requests', data);
      setIsLoading(false);

      // Invalidate related caches to refresh data
      mutate((key) => typeof key === 'string' && key.startsWith('/schedule-requests'), undefined, { revalidate: true });
      // Also invalidate unavailabilities and shifts to refresh calendar
      mutate((key) => typeof key === 'string' && key.includes('/unavailabilities'), undefined, { revalidate: true });
      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'Schedule request submitted successfully',
        data: {
          id: response.data.data.id,
          status: response.data.data.status,
          created_at: response.data.data.created_at
        }
      };
    } catch (err: any) {
      setError(err.message || 'Failed to submit schedule request');
      setIsLoading(false);
      return {
        success: false,
        message: err?.response?.data?.message || err.message || 'Failed to submit schedule request'
      };
    }
  };

  return {
    submitRequest,
    isLoading,
    error
  };
}

// ==============================|| SHIFT REQUESTS HOOK ||============================== //

interface GetRequestsParams {
  status?: 'submitted' | 'approved' | 'declined' | 'cancelled';
  type?: 'change' | 'sick' | 'day_off';
  date?: string; // YYYY-MM-DD format
  restaurant_id?: number;
  waiter_id?: number;
  is_manager_assigned?: boolean;
}

export function useGetRequests(params: GetRequestsParams = {}) {
  const { status, type, date, restaurant_id, waiter_id, is_manager_assigned } = params;
  const queryParams = new URLSearchParams();

  if (status) queryParams.set('status', status);
  if (type) queryParams.set('type', type);
  if (date) queryParams.set('date', date);
  if (restaurant_id) queryParams.set('restaurant_id', restaurant_id.toString());
  if (waiter_id) queryParams.set('waiter_id', waiter_id.toString());
  if (is_manager_assigned !== undefined) queryParams.set('is_manager_assigned', is_manager_assigned ? '1' : '0');

  const queryString = queryParams.toString();
  const url = queryString ? `/schedule-requests?${queryString}` : '/schedule-requests';

  const { data, isLoading, isValidating, error } = useSWR(url, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const requests = useMemo(() => (data?.data as ShiftRequestType[]) || [], [data?.data]);

  const submittedCount = useMemo(() => {
    return requests.filter((request) => request.status === 'submitted').length;
  }, [requests]);

  return {
    requests,
    submittedCount,
    isLoading: isLoading || isValidating,
    error
  };
}

// ==============================|| REQUEST ACTIONS HOOK ||============================== //

interface UpdateRequestParams {
  scheduleRequestId: number;
  status: 'approved' | 'declined';
  manager_comment?: string;
}

interface RequestActionResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function useRequestActions() {
  const [isLoading, setIsLoading] = useState(false);

  const updateRequest = async (params: UpdateRequestParams): Promise<RequestActionResponse> => {
    setIsLoading(true);
    try {
      const response = await axiosServices.patch(`/schedule-requests/${params.scheduleRequestId}`, {
        action: params.status === 'approved' ? 'approve' : 'decline',
        manager_comment: params.manager_comment
      });

      mutate((key) => typeof key === 'string' && key.startsWith('/schedule-requests'), undefined, { revalidate: true });

      return {
        success: true,
        message: `Request ${params.status} successfully`,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error updating request:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Failed to ${params.status} request`
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateRequest,
    isLoading
  };
}

// ==============================|| RESTAURANT SHIFTS HOOK ||============================== //

export function useRestaurantShifts(params: GetRestaurantShiftsParams) {
  const { restaurantId, waiterId, from, to, per_page = 100, timezone = undefined, revalidateIfStale = true } = params;

  const getKey = (pageIndex: number, previousPageData: RestaurantShiftsResponse | null) => {
    if (previousPageData) {
      const { meta } = previousPageData;
      const totalPages = meta.last_page;
      if (pageIndex >= totalPages) return null;
    }

    const queryParams = new URLSearchParams();

    if (waiterId) queryParams.set('waiter_id', waiterId.toString());
    if (from) queryParams.set('from', from);
    if (to) queryParams.set('to', to);
    queryParams.set('page', (pageIndex + 1).toString());
    queryParams.set('per_page', per_page.toString());

    return `/restaurants/${restaurantId}/shifts?${queryParams.toString()}`;
  };

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite(getKey, fetcher, {
    revalidateIfStale,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    initialSize: 1
  });

  useEffect(() => {
    if (data && data.length > 0) {
      const firstPage = data[0];
      const totalPages = firstPage?.meta?.last_page || 0;

      if (totalPages > 1 && size < totalPages) {
        setSize(totalPages);
      }
    }
  }, [data, size, setSize]);

  const allShifts = useMemo(() => {
    if (!data) return [];

    return data.flatMap((pageData) => pageData?.data || []);
  }, [data]);

  const memoizedValue = useMemo(() => {
    const firstPage = data?.[0];
    const meta = firstPage?.meta;

    const totalPages = meta ? meta.last_page : 0;
    const hasMorePages = firstPage && meta ? size < totalPages : false;
    const isStillLoading = isLoading || (totalPages > 1 && size < totalPages);

    const shiftsByDate: Record<string, RestaurantShift[]> = {};
    const highlightedDates: string[] = [];

    allShifts?.forEach((shift) => {
      const date = formatDateKeyWithTimezone(shift.start_at, timezone);

      // Skip shifts if date is empty (restaurant timezone not loaded yet)
      if (!date) {
        return;
      }

      if (!shiftsByDate[date]) {
        shiftsByDate[date] = [];
      }
      shiftsByDate[date].push(shift);

      if (!highlightedDates.includes(date)) {
        highlightedDates.push(date);
      }
    });

    const calendarShifts: CalendarShift[] = Object.entries(shiftsByDate).map(([date, dateShifts]) => ({
      id: parseInt(date.replace(/-/g, '')),
      date,
      shifts: dateShifts as any,
      hasShifts: true
    }));

    return {
      shifts: allShifts as RestaurantShift[],
      shiftsByDate,
      calendarShifts,
      highlightedDates,
      shiftsLoading: isStillLoading,
      shiftsError: error,
      shiftsValidating: isValidating,
      shiftsEmpty: !isStillLoading && allShifts.length === 0,
      pagination: {
        currentPage: size,
        totalPages: totalPages,
        totalItems: meta?.total || 0,
        perPage: per_page
      },
      loadMore: () => setSize(size + 1),
      hasMore: hasMorePages,
      refetch: mutate
    };
  }, [allShifts, data, error, isLoading, isValidating, size, setSize, per_page, mutate, timezone]);

  return memoizedValue;
}

// ==============================|| CREATE SHIFT HOOK ||============================== //

export function useCreateShift() {
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslation();

  const createShift = async (params: CreateShiftParams): Promise<CreateShiftResponse> => {
    setIsLoading(true);
    try {
      const payload = buildCreateShiftRequestPayload(params);

      const response = await axiosServices.post(`/waiters/${params.waiterId}/shifts`, payload);

      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'Shift created successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error creating shift:', error);
      return {
        success: false,
        message: resolveShiftApiErrorMessage(error, t, 'Failed to create shift')
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { createShift, isLoading };
}

// ==============================|| UPDATE SHIFT HOOK ||============================== //

export function useChangeShift() {
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslation();

  const updateShift = async (params: UpdateShiftParams): Promise<UpdateShiftResponse> => {
    setIsLoading(true);
    try {
      const payload = buildUpdateShiftRequestPayload(params);

      const response = await axiosServices.patch(`/shifts/${params.shiftId}`, payload);

      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'Shift updated successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error updating shift:', error);
      const errors = error?.errors;
      const statusCode = error?._status || error?.response?.status;
      if (
        statusCode === 409 ||
        (Array.isArray(errors) && errors.some((err: any) => err?.title === 'already_started' || err?.status === '409'))
      ) {
        return {
          success: false,
          message: t('Cannot update shift that has already finished')
        };
      }

      return {
        success: false,
        message: resolveShiftApiErrorMessage(error, t, 'Failed to update shift')
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { updateShift, isLoading };
}

// ==============================|| DELETE SHIFT HOOK ||============================== //

export function useDeleteShift() {
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslation();

  const deleteShift = async (shiftId: number): Promise<DeleteShiftResponse> => {
    setIsLoading(true);
    try {
      await axiosServices.delete(`/shifts/${shiftId}`);

      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });

      return {
        success: true,
        message: t('Shift deleted successfully')
      };
    } catch (error: any) {
      console.error('Error deleting shift:', error);
      const errors = error?.errors;
      const statusCode = error?._status || error?.response?.status;
      if (
        statusCode === 409 ||
        (Array.isArray(errors) && errors.some((err: any) => err?.title === 'already_started' || err?.status === '409'))
      ) {
        return {
          success: false,
          message: t('Cannot delete shift that has already finished')
        };
      }

      return {
        success: false,
        message: error?.message || error?.detail || t('Failed to delete shift')
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteShift, isLoading };
}

// ==============================|| UNAVAILABILITIES API HOOKS ||============================== //

// Get unavailabilities for a waiter
export function useGetWaiterUnavailabilities(waiterId: number | string | null, params?: { from?: string; to?: string }) {
  const shouldFetch = waiterId !== null && waiterId !== undefined;
  const queryParams = new URLSearchParams();

  if (params?.from) queryParams.set('from', params.from);
  if (params?.to) queryParams.set('to', params.to);

  const queryString = queryParams.toString();
  const url = shouldFetch ? `/waiters/${waiterId}/unavailabilities${queryString ? `?${queryString}` : ''}` : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{ data: Unavailability[] }>(url, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return {
    unavailabilities: (data?.data as Unavailability[]) || [],
    unavailabilitiesLoading: isLoading,
    unavailabilitiesError: error,
    unavailabilitiesValidating: isValidating,
    refetch: mutate
  };
}

// Get unavailabilities for a restaurant
export function useGetRestaurantUnavailabilities(
  restaurantId: number | string | null,
  params?: { from?: string; to?: string; revalidateIfStale?: boolean }
) {
  const shouldFetch = restaurantId !== null && restaurantId !== undefined;
  const queryParams = new URLSearchParams();

  if (params?.from) queryParams.set('from', params.from);
  if (params?.to) queryParams.set('to', params.to);

  const queryString = queryParams.toString();
  const url = shouldFetch ? `/restaurants/${restaurantId}/unavailabilities${queryString ? `?${queryString}` : ''}` : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{ data: Unavailability[] }>(url, fetcher, {
    revalidateIfStale: params?.revalidateIfStale ?? false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const unavailabilities = useMemo(() => {
    const items = data?.data?.filter((item) => item.status === 'approved');

    return (items as Unavailability[]) || [];
  }, [data]);

  return {
    unavailabilities,
    unavailabilitiesLoading: isLoading,
    unavailabilitiesError: error,
    unavailabilitiesValidating: isValidating,
    refetch: mutate
  };
}

// Create unavailability
export function useCreateUnavailability() {
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslation();

  const createUnavailability = async (params: CreateUnavailabilityParams): Promise<UnavailabilityResponse> => {
    setIsLoading(true);
    try {
      const payload = {
        date: params.date,
        starts_at: params.starts_at,
        ends_at: params.ends_at,
        type: params.type,
        ...(params.note && { note: params.note })
      };

      const response = await axiosServices.post(`/waiters/${params.waiterId}/unavailabilities`, payload);

      // Invalidate related caches
      mutate((key) => typeof key === 'string' && key.includes('/unavailabilities'), undefined, { revalidate: true });
      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });
      mutate((key) => typeof key === 'string' && key.includes(`/waiters/${params.waiterId}`), undefined, { revalidate: true });

      return {
        success: true,
        message: t('Unavailability created successfully'),
        data: response.data?.data
      };
    } catch (error: any) {
      console.error('Error creating unavailability:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to create unavailability'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { createUnavailability, isLoading };
}

// Update unavailability (approve/decline)
export function useUpdateUnavailability() {
  const [isLoading, setIsLoading] = useState(false);

  const updateUnavailability = async (params: UpdateUnavailabilityParams): Promise<UnavailabilityResponse> => {
    setIsLoading(true);
    try {
      const payload: any = {
        // Backend expects "action" field: approve | decline
        action: params.status === 'approved' ? 'approve' : 'decline'
      };
      if (params.note) {
        // Map local "note" to backend's manager_comment field
        payload.manager_comment = params.note;
      }

      const response = await axiosServices.patch(`/unavailabilities/${params.unavailabilityId}`, payload);

      // Invalidate related caches
      mutate((key) => typeof key === 'string' && key.includes('/unavailabilities'), undefined, { revalidate: true });
      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });

      return {
        success: true,
        message: `Unavailability ${params.status} successfully`,
        data: response.data?.data
      };
    } catch (error: any) {
      console.error('Error updating unavailability:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || `Failed to ${params.status} unavailability`
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { updateUnavailability, isLoading };
}

// Delete unavailability entry
export function useDeleteUnavailability() {
  const [isLoading, setIsLoading] = useState(false);

  const deleteUnavailability = async (unavailabilityId: number): Promise<DeleteUnavailabilityResponse> => {
    setIsLoading(true);
    try {
      // Instead of hard delete, cancel unavailability via PATCH action=cancel
      await axiosServices.patch(`/unavailabilities/${unavailabilityId}`, {
        action: 'cancel'
      });

      mutate((key) => typeof key === 'string' && key.includes('/unavailabilities'), undefined, { revalidate: true });
      mutate((key) => typeof key === 'string' && key.includes('/shifts'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'Unavailability cancelled successfully'
      };
    } catch (error: any) {
      console.error('Error deleting unavailability:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to delete unavailability'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteUnavailability, isLoading };
}

// ==============================|| CREATE DAY OFF HOOK (DEPRECATED - use useCreateUnavailability) ||============================== //

interface CreateDayOffParams {
  waiterId: number;
  date: string; // YYYY-MM-DD format
}

interface CreateDayOffResponse {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * @deprecated Use useCreateUnavailability instead with type: 'day_off'
 */
export function useCreateDayOff() {
  const { createUnavailability, isLoading } = useCreateUnavailability();
  const t = useTranslation();

  const createDayOff = async (params: CreateDayOffParams): Promise<CreateDayOffResponse> => {
    // Convert to new API format
    const dateObj = new Date(params.date);
    const startOfDay = new Date(dateObj);
    startOfDay.setHours(0, 10, 0, 0);
    const endOfDay = new Date(dateObj);
    endOfDay.setHours(23, 59, 59, 999);

    const result = await createUnavailability({
      waiterId: params.waiterId,
      date: params.date,
      starts_at: startOfDay.toISOString(),
      ends_at: endOfDay.toISOString(),
      type: 'day_off'
    });

    return {
      success: result.success,
      message: result.success ? t('Day off assigned successfully') : result.message,
      data: result.data
    };
  };

  return { createDayOff, isLoading };
}
